import React, { useMemo, useState, useRef } from "react";
import { useTranslation } from "react-i18next";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faPlus } from "@fortawesome/pro-regular-svg-icons";
import { arrayMove } from "../../../utilities/utils";
import { CardHeading, Divider } from "../profile/index.style";
import { Address1 } from "./addresses.style";
import { useMutation } from "../../../GraphQL/helpers";
import { usePerfil } from "../../../contexts/pages/perfilContext";
import { Row, Column } from "../../global/grid";
import AdaHelper from "../../global/adaHelper";
import Panel, { PanelHeader, PanelHeaderActions, PanelBody } from "../../global/panel";
import ButtonLink from "../../global/buttonLink";
import Badge from "../../global/badge";
import EmptyBox from "../../global/emptyBox";
import AddressModal from "../addressModal";
import DeleteAddressModal from "../deleteAddressModal";
import GET_PROFILE from "../../../GraphQL/queries/fetchProfile";
import { MAKE_PRIMARY_ADDRESS } from "../../../GraphQL/mutations/address";
import errorHandler from "../../../utilities/errorHandler";

const Addresses = (props) => {
    const { t } = useTranslation("components.profile.addresses");
    const [updateAddress] = useMutation(MAKE_PRIMARY_ADDRESS);
    const { setError } = usePerfil();
    const [isNewAddress, setNewAddress] = useState(false);
    const [selectedAddress, setEditAddress] = useState({});
    const [toBeDeletedAddress, setDeleteAddress] = useState({});
    const addressModal = useRef(null);
    const deleteAddressModal = useRef(null);
    const addresses = useMemo(() => {
        if (props.addresses) {
            const mainAddressIndex = props.addresses.findIndex(a => a.principal);

            if (mainAddressIndex > -1) {
                return arrayMove(props.addresses, mainAddressIndex, 0);
            }

            return props.addresses;
        }

        return [];
    }, [props.addresses]);

    const addAddress = (e) => {
        setNewAddress(true);
        setEditAddress({ principal: !props.addresses.length });
        addressModal?.current && addressModal.current.open();
    };

    const editAddress = (address) => {
        setNewAddress(false);
        setEditAddress(address);
        addressModal?.current && addressModal.current.open();
    };

    const deleteAddress = (address) => {
        setDeleteAddress(address);
        deleteAddressModal?.current && deleteAddressModal.current.open();
    };

    const makePrimary = async (address) => {
        try {
            const { data: resultData } = await updateAddress({
                variables: {
                    id: address.id,
                    principal: true,
                },
                optimisticResponse: {
                    editarDireccion: {
                        direccion: {
                            id: address.id,
                            principal: true,
                            __typename: "Direccion",
                        },
                        errores: null,
                    }
                },
                update(cache, { data: { editarDireccion } }) {
                    if (editarDireccion?.direccion?.principal ?? false) {
                        const { perfil } = cache.readQuery({ query: GET_PROFILE });
                        const currentMainAddress = perfil.direcciones.find(d => d.principal && d.id !== editarDireccion.direccion.id);

                        if (currentMainAddress.id !== editarDireccion.direccion.id) {
                            cache.modify({
                                id: cache.identify(currentMainAddress),
                                fields: { principal: () => false },
                            });
                        }
                    }
                },
            });
            const { errores } = resultData.editarDireccion;

            if (errores) {
                setError({
                    title: t("errors.makePrimary.default.title"),
                    message: errores[0].mensaje,
                });
            }
        } catch (error) {
            errorHandler(error, () => {
                setError(t("errors.makePrimary.default", { returnObjects: true }));
            });
        }
    };

    const onModalClose = () => {
        setNewAddress(false);
    };

    return (
        <>
            <Panel variant="shadowed">
                <PanelHeader>
                    <CardHeading>{t("title")}</CardHeading>
                    <PanelHeaderActions>
                        <ButtonLink onClick={addAddress}>
                            <FontAwesomeIcon icon={faPlus} />
                            <AdaHelper>{t("addAddressAda")}</AdaHelper>
                        </ButtonLink>
                    </PanelHeaderActions>
                </PanelHeader>
                <PanelBody>
                    {!addresses.length && <EmptyBox>
                        <ButtonLink onClick={addAddress}>{t("add")}</ButtonLink>
                    </EmptyBox>}

                    {addresses.map((a) => (
                        <Row pb={{ _: 4 }} key={a.id}>
                            <Column lg="7">
                                <Address1>{a.direccion}</Address1>
                                {a.distrito && <div>{a.distrito}</div>}
                                <div>{a.ciudad}, {a.departamento}</div>
                                <div>{a.pais}</div>
                                {a.principal && <Badge colors="primaryWithBg" mr={2}>{t("primary")}</Badge>}
                                {!a.principal && <ButtonLink onClick={() => makePrimary(a)}>{t("makePrimary")}</ButtonLink>}
                            </Column>
                            {!a.principal && (
                                <Column lg="5" textAlign={{ lg: "right" }}>
                                    <ButtonLink onClick={() => editAddress(a)}>{t("edit")}</ButtonLink>
                                    <Divider>|</Divider>
                                    <ButtonLink onClick={() => deleteAddress(a)}> {t("delete")}</ButtonLink>
                                </Column>
                            )}
                        </Row>
                    ))}
                </PanelBody>
            </Panel>
            <AddressModal onClosed={onModalClose} isNewAddress={isNewAddress}
                          address={selectedAddress} innerRef={addressModal} />
            <DeleteAddressModal address={toBeDeletedAddress} innerRef={deleteAddressModal} />
        </>
    );
};

export default Addresses;
